<script setup>

import {computed, onMounted, ref} from "vue";

const currentEmailListSize = ref(1000);
const currentManualSendRate = ref(200);
const averageOpenRate = ref(25);
const averageClickThroughRate = ref(5);
const averageConversionRate = ref(10);
const averageOrderValue = ref(100);
const expectedImprovement = ref(50);

const transitioned = ref(false)
const calculating = ref(false);
const seeResult = ref(false);

const results = computed(() => {
    // New send rate with automation (improvement applies to send rate)
    const newSendRate = currentManualSendRate.value * (1 + expectedImprovement.value / 100);

    // Additional emails sent per month
    const additionalEmails = newSendRate - currentManualSendRate.value;

    // Current performance
    const currentOpens = currentManualSendRate.value * (averageOpenRate.value / 100);
    const currentClicks = currentOpens * (averageClickThroughRate.value / 100);
    const currentConversions = currentClicks * (averageConversionRate.value / 100);

    // New performance with automation
    const newOpens = newSendRate * (averageOpenRate.value / 100);
    const newClicks = newOpens * (averageClickThroughRate.value / 100);
    const newConversions = newClicks * (averageConversionRate.value / 100);

    // Additional metrics
    const additionalOpens = newOpens - currentOpens;
    const additionalClicks = newClicks - currentClicks;
    const additionalConversions = newConversions - currentConversions;

    // Additional revenue per month
    const additionalRevenuePerMonth = additionalConversions * averageOrderValue.value;

    // Annual revenue increase
    const annualRevenueIncrease = additionalRevenuePerMonth * 12;

    return {
        additionalEmails: additionalEmails.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        additionalEmailsRaw: additionalEmails,
        additionalOpens: additionalOpens.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        additionalOpensRaw: additionalOpens,
        additionalClicks: additionalClicks.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        additionalClicksRaw: additionalClicks,
        additionalConversions: additionalConversions.toLocaleString('en-US', {maximumFractionDigits:1}).replaceAll(',', ' '),
        additionalConversionsRaw: additionalConversions,
        additionalRevenuePerMonth: additionalRevenuePerMonth.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        additionalRevenuePerMonthRaw: additionalRevenuePerMonth,
        annualRevenueIncrease: annualRevenueIncrease.toLocaleString('en-US', {maximumFractionDigits:0}).replaceAll(',', ' '),
        annualRevenueIncreaseRaw: annualRevenueIncrease,
    };
})

const calculateInitial = () => {
    calculating.value = true

    setTimeout(function(){
        seeResult.value = true;

        setTimeout(function() {
            window.scrollBy({
                top: 500,
                behavior: 'smooth'
            });
        }, 300);
    }, 300)
}

onMounted(() => {
    setTimeout(() => {
        transitioned.value = true;
    }, 100);
})

</script>

<template>
    <div>
        <card-basic
            class="transition-all duration-500"
            :class="transitioned ? 'translate-y-0 scale-100' : '-translate-y-4 opacity-0 scale-95'"
        >
            <div class="grid-cols-2 gap-8 md:grid">
                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Current email list size
                    </p>
                    <input-number
                        max="10000000"
                        min="0"
                        :step="100"
                        class="mx-auto w-36"
                        v-model:content="currentEmailListSize"
                        :show-step-ui="true"
                        placeholder="List size"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Current manual email send rate per month
                    </p>
                    <input-number
                        max="1000000"
                        min="0"
                        :step="10"
                        class="mx-auto w-36"
                        v-model:content="currentManualSendRate"
                        :show-step-ui="true"
                        placeholder="Emails per month"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average open rate (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="averageOpenRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="averageOpenRate = $event"
                        />{{ averageOpenRate }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average click-through rate (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="averageClickThroughRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="averageClickThroughRate = $event"
                        />{{ averageClickThroughRate }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average conversion rate from email (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="averageConversionRate"
                            min="0"
                            max="100"
                            :step="0.1"
                            class="w-full"
                            @update:content="averageConversionRate = $event"
                        />{{ averageConversionRate }} %
                    </div>
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Average order value from email conversions
                    </p>
                    <input-number
                        max="100000"
                        min="0"
                        :step="5"
                        class="mx-auto w-36"
                        v-model:content="averageOrderValue"
                        :show-step-ui="true"
                        placeholder="Order value"
                    />
                </div>

                <div
                    class="col-span-2 p-2 text-center"
                >
                    <p class="mb-2">
                        Expected improvement with automation (%)
                    </p>
                    <div class="mx-auto md:w-64">
                        <input-range
                            :content="expectedImprovement"
                            min="0"
                            max="500"
                            :step="1"
                            class="w-full"
                            @update:content="expectedImprovement = $event"
                        />{{ expectedImprovement }} %
                    </div>
                </div>

                <div
                    v-if="! seeResult"
                    class="flex justify-center col-span-2"
                >
                    <button-primary
                        class="justify-center w-48 mx-auto transition-all"
                        :class="calculating ? 'translate-y-8 opacity-0' : ''"
                        @click="calculateInitial"
                    >
                        Calculate
                    </button-primary>
                </div>

                <div
                    v-if="calculating"
                    class="col-span-2 -mx-4 -mt-6 -mb-4 transition-colors duration-1000 text-wlPrimaryContrast md:-mx-6 md:-mb-6 lg:-mx-8 lg:-mb-8"
                    :class="seeResult ? 'bg-wlPrimary' : 'bg-transparent'"
                >
                    <div class="w-full h-12 transform -translate-y-px bg-white rounded-b-wl"></div>
                    <div
                        class="col-span-2 p-4 pt-3 transition-all duration-1000 rounded-b-wl md:p-6 md:pt-4 lg:p-8 lg:pt-5"
                        :class="seeResult ? 'translate-y-0 opacity-100 max-h-screen' : '-translate-y-8 opacity-0 max-h-0'"
                    >
                        <div class="flex mb-8 space-x-4">
                            <div>
                                <i class="mt-1 text-2xl fa-regular fa-chart-line"></i>
                            </div>
                            <div>
                                <p>With email automation, you would send <strong>{{ results.additionalEmails }} additional emails per month</strong>.</p>

                                <p>This would result in <strong>{{ results.additionalOpens }} additional opens per month</strong>.</p>

                                <p>You would get <strong>{{ results.additionalClicks }} additional clicks per month</strong>.</p>

                                <p>This would lead to <strong>{{ results.additionalConversions }} additional conversions per month</strong>.</p>

                                <p>Additional revenue per month: <strong>${{ results.additionalRevenuePerMonth }}</strong></p>

                                <p class="mt-4">
                                    <strong>Annual revenue increase: ${{ results.annualRevenueIncrease }}</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </card-basic>
    </div>
</template>

<style scoped>

</style>

